import { useState, useRef, useEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import ChatIcon from '@mui/icons-material/Chat';
import CloseIcon from '@mui/icons-material/CloseRounded';
import { Alert, Button, IconButton } from '@mui/material';
import CropFreeIcon from '@mui/icons-material/CropFree';
import CropDinIcon from '@mui/icons-material/CropDin';
import ErrorIcon from '@mui/icons-material/Error';
import SendIcon from '@mui/icons-material/Send';
import rehypeRaw from "rehype-raw";

import styles from './ChatWidget.module.scss';
import RecaptchaText from './RecaptchaText';
interface Message {
    sender: "agent" | "user" | "thinking";
    message: string;
}

interface ChatWidgetProps {
    chatbotId: string;
    tenantId: string;
    apiKey: string;
    color: string;
    backgroundColor: string;
    headline: string;
    startMessage: string;
    endpoint: string;
}

function ChatWidget({
    color,
    backgroundColor,
    headline,
    startMessage,
    chatbotId,
    tenantId,
    apiKey,
    endpoint,
}: ChatWidgetProps) {

    color = color ?? "#FFFFFF";
    backgroundColor = backgroundColor ?? "#5A3EF0";
    headline = headline ?? "Ask me a question.";
    startMessage = startMessage ?? "Ask me a question.";

    const [isMaximized, setIsMaximized] = useState(false);
    const [open, setOpen] = useState(false);
    const [closeIconVisible, setCloseIconVisible] = useState(false);
    const [openIconVisible, setOpenIconVisible] = useState(true);
    const [messages, setMessages] = useState<Message[]>([]);
    const [input, setInput] = useState('');
    const [isSending, setIsSending] = useState(false);
    const [isValidChatbot, setIsValidChatbot] = useState(false);
    const [maxMessageSize, setMaxMessageSize] = useState<number | null>(null);
    const [sessionId, setSessionId] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    const [recaptchaSiteKey, setRecaptchaSiteKey] = useState(null);

    const widgetRef = useRef(null);

    const endRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        getHistory();
    }, []);

    useEffect(() => {
        if (recaptchaSiteKey) {
            recaptchaIsWorking().catch(() => setError("Recaptcha is not valid. Please check your settings."))
        }
    }, [recaptchaSiteKey]);

    useEffect(() => {
        endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, [messages, open]);

    const toggleSize = () => {
        setIsMaximized(!isMaximized);
    }

    const openWidget = () => {
        setOpen(true);
        setOpenIconVisible(false);
        setTimeout(() => setCloseIconVisible(true), 0);
    }

    const closeWidget = () => {
        setOpen(false);
        setCloseIconVisible(false);
        setTimeout(() => setOpenIconVisible(true), 0);
    }

    function toggle() {
        if (open) {
            closeWidget();
        } else {
            openWidget();
        }
    }

    // create cookie
    const setCookie = (name: string, value: string, hours: number) => {
        const date = new Date();
        date.setTime(date.getTime() + hours * 60 * 60 * 1000);
        const expires = "expires=" + date.toUTCString();
        document.cookie = `${name}=${value}; ${expires}; path=/`;
    }

    // read cookie
    const getCookie = (name: string): string | null => {
        const match = document.cookie.match(
            new RegExp("(^| )" + name + "=([^;]+)")
        );
        const value: string | null = match ? decodeURIComponent(match[2]) : null;

        if (value === "undefined") {
            return null;
        }

        return value;
    }

    const getHistory = useCallback(async () => {
        try {
            const res = await window.fetch(`${endpoint}/api/knowflow/chatbot/${chatbotId}/history`, {
                method: 'POST',
                credentials: 'same-origin',
                headers: {
                    'Content-Type': 'application/json',
                    'X-API-KEY': apiKey,
                    'tenant': tenantId,
                },
                body: JSON.stringify({ sessionId: getCookie("ailigKnowflowChatbotSessionId") })
            });


            const data = await res.json();

            setSessionId(getCookie("ailigKnowflowChatbotSessionId"));

            if (data.maxMessageSize) {
                setIsValidChatbot(true);
                setMaxMessageSize(data.maxMessageSize);
            }

            if (data.captchaConfig?.recaptchaSiteKey) {
                setRecaptchaSiteKey(data.captchaConfig.recaptchaSiteKey);
            }

            if (data.messages && Array.isArray(data.messages)) {
                setMessages([{ sender: 'agent', message: startMessage }, ...data.messages]);
            }
        } catch (error) {
            console.error("Error fetching chat history:", error);
        }
    }, []);

    const getRecaptchaToken = async (): Promise<string | undefined> => {
        return new Promise((resolve) => {
            window.grecaptcha.ready(async () => {
                const token = await window.grecaptcha.execute(recaptchaSiteKey!, { action: "chatWidgetSend" });
                resolve(token);
            });
        });
    }


    const recaptchaIsWorking = async (): Promise<void> => {
        return new Promise((resolve, reject) => {
            if (!window.grecaptcha) {
                reject();
            }
            window.grecaptcha.ready(async () => {
                try {
                    var token = await window.grecaptcha.execute(recaptchaSiteKey!, { action: "chatWidgetSend" });
                    if (token) {
                        resolve();
                    } else {
                        reject();
                    }
                } catch (e) {
                    console.log(e);
                    reject();
                }
            });
        });
    }

    const sendToServer = async () => {
        const body: {
            message: string;
            sessionId: string | null;
            token?: string | undefined;
        } = {
            message: input,
            sessionId: sessionId ?? null,
        };

        try {
            setIsSending(true);
            setError(null);

            if (recaptchaSiteKey && window.grecaptcha && !body.sessionId) {
                body.token = await getRecaptchaToken();
            }

            const res = await window.fetch(`${endpoint}/api/knowflow/chatbot/${chatbotId}`, {
                method: 'POST',
                credentials: 'same-origin',
                headers: {
                    'Content-Type': 'application/json',
                    'X-API-KEY': apiKey,
                    'tenant': tenantId,
                },
                body: JSON.stringify(body),
            });
            setIsSending(false);
            const data = await res.json();
            if (res.status == 200) {
                setInput('');
                setCookie("ailigKnowflowChatbotSessionId", data.sessionId, 1);
                setSessionId(data.sessionId);
                setMessages(prev => [...prev, { sender: 'agent', message: data.message }]);
            } else {
                console.log(data.message)
                setError("The chatbot is temporary currently not available");
            }


        } catch (error) {
            setIsSending(false);
            console.log("Error sending", error);
        }
    };

    const send = async () => {
        if (!input.trim()) return;

        setMessages(prev => [...prev, { sender: 'user', message: input }]);

        await sendToServer();
    }

    if (!isValidChatbot) {
        return <></>;
    }

    return <>
        <button
            className={`${styles.chatLauncher} knowflow-chat-launcher`}
            style={{ backgroundColor, color }}
            onClick={toggle}
        >
            <div className={`${styles.icon} ${openIconVisible ? styles.visible : ""} knowflow-chat-icon`}>
                <ChatIcon fontSize="large" />
            </div>
            <div className={`${styles.icon} ${closeIconVisible ? styles.visible : ""} knowflow-chat-icon`}><CloseIcon fontSize="large" /></div>
        </button>

        <div
            className={`${styles.chatWidget} knowflow-chat-widget ${open ? styles.open : ""} ${isMaximized ? styles.maximized : ""}`}
            ref={widgetRef}
        >
            <div className={`${styles.chatHeader} knowflow-chat-header`} style={{ backgroundColor: color, color: backgroundColor }}>
                {headline ?? "Chat"}
                <div>
                    <IconButton
                        onClick={toggleSize}
                        className={styles.maximizeButton}
                        sx={{ color: backgroundColor, background: 'transparent' }}
                    >
                        {isMaximized ? (
                            <CropDinIcon fontSize="small" />
                        ) : (
                            <CropFreeIcon fontSize="small" />
                        )}
                    </IconButton>
                    <IconButton onClick={toggle} >
                        <CloseIcon className={styles.closeButton} style={{ color: backgroundColor }} fontSize="small" />
                    </IconButton>
                </div>
            </div>

            <div className={`${styles.chatBody} knowflow-chat-body`}>

                {!sessionId && !isSending && recaptchaSiteKey && <RecaptchaText />}
                {messages.map(({ sender, message }, i) => (
                    <div
                        ref={i === messages.length - 1 ? endRef : null}
                        key={i}
                        className={`${styles.bubbleContainer} ${sender === 'user' ? styles.userBubble : styles.botBubble
                            }`}
                    >
                        {sender === 'agent' && (
                            <div className={`${styles.botAvatar} knowflow-chat-avatar`} style={{
                                backgroundColor: backgroundColor
                            }} />
                        )}
                        <div
                            className={`${styles.bubble} knowflow-chat-bubble`}
                            style={{
                                backgroundColor: sender === 'agent' ? '#eee' : backgroundColor,
                                color: sender === 'agent' ? '#000' : color,
                            }}
                        >
                            <ReactMarkdown rehypePlugins={[rehypeRaw]} remarkPlugins={[remarkGfm]}>
                                {message.replaceAll(/<br>+$/g, '')}
                            </ReactMarkdown>
                        </div>
                    </div>
                ))}

                {error != null &&
                    <Alert className={styles.errorContainer} icon={<ErrorIcon fontSize="inherit" />} severity="error" action={<Button color="inherit" variant='outlined' size="small" onClick={sendToServer} sx={{
                        textTransform: 'none',
                        alignSelf: 'center',
                        marginTop: '-4px',
                    }}>Retry</Button>}>
                        {error}
                    </Alert>
                }

                {isSending && <div
                    className={`${styles.bubbleContainer} ${styles.botBubble} knowflow-chat-sending-bubble`}
                >
                    <div className={`${styles.botAvatar} knowflow-chat-avatar`} style={{
                        backgroundColor: backgroundColor
                    }} />
                    <div
                        className={styles.bubble}
                        style={{
                            width: 'auto',
                            backgroundColor: '#eee',
                            color: "#000",
                        }}
                    >
                        <TypingIndicator />
                    </div>
                </div>}
            </div>

            <div className={`${styles.chatInput} knowflow-chat-input ${isSending ? styles.disabled : ''}`}>
                <textarea
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), send())}
                />
                <div>
                    <IconButton onClick={send} className={`${styles.sendButton} ${input.length >= maxMessageSize! ? styles.disabled : ''}`} ><SendIcon style={{ color: backgroundColor, }} fontSize='medium' /></IconButton>
                    <div className={`${styles.charCount} ${input.length >= maxMessageSize! ? styles.tooMuch : ''}`}>
                        {input.length.toString()
                            .replace(/\B(?=(\d{3})+(?!\d))/g, ".")}/{maxMessageSize!.toString()
                                .replace(/\B(?=(\d{3})+(?!\d))/g, ".")}
                    </div>
                </div>
            </div>
        </div>
    </>;

}

export default ChatWidget;

const TypingIndicator = () => {
    return (
        <div className={styles.typeIndicator}>
            <div className={styles.dot} style={{ animationDelay: '0s' }}></div>
            <div className={styles.dot} style={{ animationDelay: '0.2s' }}></div>
            <div className={styles.dot} style={{ animationDelay: '0.4s' }}></div>
        </div>
    );
};